import { useState, useEffect, useRef } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { motion, AnimatePresence } from "framer-motion";
import ReactMarkdown from "react-markdown";
import {
  Microphone, SpinnerGap, Play, User, Users, UsersThree, SpeakerHigh,
  Download, FileText, Copy, Check, MusicNote, Pause, Paperclip, X, TextT, CloudArrowUp,
  ClockCounterClockwise, Plus,
} from "@phosphor-icons/react";
import { StudioPodcastHistory } from "./StudioPodcastHistory";

function CopyBtn({ text }) {
  const [copied, setCopied] = useState(false);
  return (
    <button onClick={() => { navigator.clipboard.writeText(text); setCopied(true); setTimeout(() => setCopied(false), 2000); }}
      className="p-1.5 rounded-md text-[var(--text-muted)] hover:text-[var(--text-primary)] hover:bg-[var(--bg-hover)] transition-colors" title="Copy">
      {copied ? <Check size={14} className="text-green-400" /> : <Copy size={14} />}
    </button>
  );
}

const INPUT_MODES = [
  { key: "topic", label: "Topic / Prompt", icon: TextT, desc: "Describe what you want — AI builds the script" },
  { key: "upload", label: "Upload File", icon: CloudArrowUp, desc: "PDF, Word, TXT — we extract & convert" },
  { key: "paste", label: "Paste Content", icon: FileText, desc: "Copy/paste article, notes, or raw text" },
];

const PODCAST_MODES = [
  { key: "solo", label: "Solo", icon: User, desc: "One speaker, monologue/teaching style", defaultSpeakers: ["Host"] },
  { key: "duo", label: "Duo / Interview", icon: Users, desc: "Two-person conversation with natural back-and-forth", defaultSpeakers: ["Host", "Guest"] },
  { key: "panel", label: "Panel", icon: UsersThree, desc: "3+ speakers, roundtable discussion", defaultSpeakers: ["Moderator", "Expert 1", "Expert 2"] },
];

const AUDIO_FORMATS = [
  { key: "mp3", label: "MP3", desc: "Universal — works everywhere" },
  { key: "wav", label: "WAV", desc: "Uncompressed — studio quality" },
  { key: "m4a", label: "M4A", desc: "Apple/AAC — smaller, great quality" },
];

export function StudioPodcastTab({ api, availableModels, defaultModel }) {
  const [view, setView] = useState("create"); // "create" | "history"
  const [voices, setVoices] = useState([]);
  const [loadingVoices, setLoadingVoices] = useState(false);

  // Input
  const [inputMode, setInputMode] = useState("topic");
  const [topicText, setTopicText] = useState("");
  const [pasteText, setPasteText] = useState("");
  const [uploadedFile, setUploadedFile] = useState(null); // { name, content }
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);

  // Config
  const [podcastMode, setPodcastMode] = useState("solo");
  const [speakerNames, setSpeakerNames] = useState(["Host"]);
  const [episodeTitle, setEpisodeTitle] = useState("");
  const [duration, setDuration] = useState(10);
  const [aiModel, setAiModel] = useState(defaultModel);
  const [generatingScript, setGeneratingScript] = useState(false);

  // Output
  const [project, setProject] = useState(null);
  const [voiceAssignments, setVoiceAssignments] = useState({});
  const [generatingAudio, setGeneratingAudio] = useState(false);
  const [audioSegments, setAudioSegments] = useState([]);
  const [playingPreview, setPlayingPreview] = useState("");

  // Merge + Export
  const [audioFormat, setAudioFormat] = useState("mp3");
  const [merging, setMerging] = useState(false);
  const [mergedAudio, setMergedAudio] = useState(null);
  const [playingEpisode, setPlayingEpisode] = useState(false);
  const [episodeAudioEl, setEpisodeAudioEl] = useState(null);
  const [generatingNotes, setGeneratingNotes] = useState(false);
  const [showNotes, setShowNotes] = useState("");

  useEffect(() => {
    const fetchVoices = async () => {
      setLoadingVoices(true);
      try { const resp = await api.get("/content-studio/podcast/voices"); setVoices(resp.data.voices || []); }
      catch (err) { if (err.response?.status !== 500) toast.error("Failed to load ElevenLabs voices — check API key in Settings"); }
      setLoadingVoices(false);
    };
    fetchVoices();
  }, [api]);

  // Sync speakers when podcast mode changes
  const handleModeChange = (mode) => {
    setPodcastMode(mode);
    const modeConfig = PODCAST_MODES.find(m => m.key === mode);
    setSpeakerNames(modeConfig?.defaultSpeakers || ["Host"]);
  };

  const addSpeaker = () => {
    if (speakerNames.length >= 6) { toast.error("Max 6 speakers"); return; }
    setSpeakerNames(prev => [...prev, `Speaker ${prev.length + 1}`]);
  };
  const updateSpeaker = (idx, name) => setSpeakerNames(prev => prev.map((s, i) => i === idx ? name : s));
  const removeSpeaker = (idx) => { if (speakerNames.length <= 1) return; setSpeakerNames(prev => prev.filter((_, i) => i !== idx)); };

  // File upload handler
  const handleFileSelect = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    e.target.value = "";
    const ext = file.name.split(".").pop()?.toLowerCase();
    const clientTypes = ["txt", "md", "markdown", "json", "csv"];
    const serverTypes = ["pdf", "docx", "xlsx", "pptx"];

    if (clientTypes.includes(ext)) {
      const text = await file.text();
      setUploadedFile({ name: file.name, content: text.slice(0, 50000) });
      toast.success(`Loaded: ${file.name}`);
    } else if (serverTypes.includes(ext)) {
      setUploading(true);
      try {
        const formData = new FormData();
        formData.append("file", file);
        const resp = await api.post("/chat/extract-file", formData, { headers: { "Content-Type": "multipart/form-data" } });
        setUploadedFile({ name: file.name, content: resp.data.text });
        toast.success(`Extracted ${resp.data.char_count.toLocaleString()} chars from ${file.name}`);
      } catch (err) { toast.error(err.response?.data?.detail || "Failed to process file"); }
      finally { setUploading(false); }
    } else {
      toast.error("Unsupported file type. Use PDF, Word, TXT, MD, or JSON.");
    }
  };

  // Resolve source text based on input mode
  const getSourceText = () => {
    if (inputMode === "topic") return topicText.trim();
    if (inputMode === "upload") return uploadedFile?.content || "";
    if (inputMode === "paste") return pasteText.trim();
    return "";
  };

  const handleGenerateScript = async () => {
    const source = getSourceText();
    if (!source || source.length < 10) { toast.error("Add source material (at least 10 characters)"); return; }
    if (!aiModel) { toast.error("Select an AI model"); return; }
    setGeneratingScript(true); setProject(null); setAudioSegments([]); setMergedAudio(null); setShowNotes("");
    try {
      const resp = await api.post("/content-studio/podcast/generate-script", {
        source_text: source, speaker_mode: podcastMode,
        speaker_names: speakerNames.filter(s => s.trim()),
        episode_title: episodeTitle.trim(), episode_style: "conversational",
        target_duration_minutes: duration, ai_model: aiModel,
      });
      setProject(resp.data);
      const defaults = {};
      (resp.data.speaker_names || []).forEach(name => { defaults[name] = ""; });
      setVoiceAssignments(defaults);
      toast.success("Script generated!");
    } catch (err) { toast.error(err.response?.data?.detail || "Script generation failed"); }
    setGeneratingScript(false);
  };

  const handleGenerateAudio = async () => {
    if (!project?.project_id) return;
    const unassigned = Object.entries(voiceAssignments).filter(([, v]) => !v);
    if (unassigned.length > 0) { toast.error(`Assign voices to: ${unassigned.map(([k]) => k).join(", ")}`); return; }
    setGeneratingAudio(true); setAudioSegments([]); setMergedAudio(null);
    try {
      const resp = await api.post("/content-studio/podcast/generate-audio", {
        project_id: project.project_id, voice_assignments: voiceAssignments,
      });
      setAudioSegments(resp.data.segments || []);
      toast.success(`Audio generated! ${resp.data.successful_segments}/${resp.data.total_segments} segments`);
    } catch (err) { toast.error(err.response?.data?.detail || "Audio generation failed"); }
    setGeneratingAudio(false);
  };

  const handleMergeAudio = async () => {
    if (!project?.project_id || audioSegments.length === 0) return;
    const validSegments = audioSegments.filter(s => s.audio_base64);
    if (validSegments.length === 0) { toast.error("No valid audio segments to merge"); return; }
    setMerging(true);
    try {
      const resp = await api.post("/content-studio/podcast/merge-audio", {
        project_id: project.project_id, segments: validSegments, audio_format: audioFormat,
      });
      setMergedAudio(resp.data);
      toast.success(`Episode merged as ${audioFormat.toUpperCase()}!`);
    } catch (err) { toast.error(err.response?.data?.detail || "Merge failed"); }
    setMerging(false);
  };

  const handleGenerateShowNotes = async () => {
    if (!project?.project_id || !aiModel) return;
    setGeneratingNotes(true);
    try {
      const resp = await api.post("/content-studio/podcast/generate-show-notes", {
        project_id: project.project_id, ai_model: aiModel,
      });
      setShowNotes(resp.data.show_notes || "");
      toast.success("Show notes generated!");
    } catch (err) { toast.error(err.response?.data?.detail || "Show notes generation failed"); }
    setGeneratingNotes(false);
  };

  const playPreview = (url) => {
    if (playingPreview === url) { setPlayingPreview(""); return; }
    setPlayingPreview(url);
    const audio = new Audio(url);
    audio.onended = () => setPlayingPreview("");
    audio.play().catch(() => setPlayingPreview(""));
  };

  const b64ToBlob = (b64, type = "audio/mpeg") => {
    const bin = atob(b64); const bytes = new Uint8Array(bin.length);
    for (let i = 0; i < bin.length; i++) bytes[i] = bin.charCodeAt(i);
    return new Blob([bytes], { type });
  };

  const playSegment = (b64) => { new Audio(URL.createObjectURL(b64ToBlob(b64))).play().catch(() => {}); };
  const downloadBlob = (blob, filename) => { const a = document.createElement("a"); a.href = URL.createObjectURL(blob); a.download = filename; a.click(); };

  const downloadShowNotes = (format) => {
    if (!showNotes) return;
    const title = episodeTitle || "episode";
    const safeName = title.replace(/\s+/g, "-").toLowerCase();
    if (format === "md") {
      downloadBlob(new Blob([showNotes], { type: "text/markdown" }), `${safeName}-show-notes.md`);
    } else if (format === "txt") {
      const plain = showNotes.replace(/\*\*/g, "").replace(/#{1,6}\s/g, "").replace(/[>-]\s/g, "");
      downloadBlob(new Blob([plain], { type: "text/plain" }), `${safeName}-show-notes.txt`);
    } else if (format === "json") {
      const data = { episode_title: title, show_notes: showNotes, generated_at: new Date().toISOString() };
      downloadBlob(new Blob([JSON.stringify(data, null, 2)], { type: "application/json" }), `${safeName}-show-notes.json`);
    }
  };

  const togglePlayEpisode = () => {
    if (playingEpisode && episodeAudioEl) { episodeAudioEl.pause(); setPlayingEpisode(false); return; }
    if (!mergedAudio?.merged_audio_base64) return;
    const mime = mergedAudio.mime_type || "audio/mpeg";
    const audio = new Audio(URL.createObjectURL(b64ToBlob(mergedAudio.merged_audio_base64, mime)));
    audio.onended = () => setPlayingEpisode(false);
    audio.play().catch(() => {});
    setEpisodeAudioEl(audio);
    setPlayingEpisode(true);
  };

  const Label = ({ children }) => (
    <label className="block text-[10px] font-bold uppercase tracking-[0.15em] text-[var(--text-muted)] mb-1.5">{children}</label>
  );

  const successfulSegments = audioSegments.filter(s => s.audio_base64);
  const sourceReady = getSourceText().length >= 10;

  return (
    <div className="space-y-6 max-w-3xl">

      {/* ── View Toggle ── */}
      <div className="flex items-center gap-2" data-testid="podcast-view-toggle">
        <button onClick={() => setView("create")}
          className={`flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm font-medium transition-all ${view === "create"
            ? "bg-[#FF4400] text-white"
            : "bg-[var(--bg-card)] border border-[var(--border)] text-[var(--text-secondary)] hover:text-[var(--text-primary)]"
          }`} data-testid="podcast-view-create">
          <Plus size={14} weight={view === "create" ? "bold" : "regular"} /> New Episode
        </button>
        <button onClick={() => setView("history")}
          className={`flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm font-medium transition-all ${view === "history"
            ? "bg-[#FF4400] text-white"
            : "bg-[var(--bg-card)] border border-[var(--border)] text-[var(--text-secondary)] hover:text-[var(--text-primary)]"
          }`} data-testid="podcast-view-history">
          <ClockCounterClockwise size={14} weight={view === "history" ? "bold" : "regular"} /> Episode History
        </button>
      </div>

      {view === "history" ? (
        <StudioPodcastHistory api={api} />
      ) : (
      <>

      {/* ── Input Mode ── */}
      <div>
        <Label>How do you want to start?</Label>
        <div className="grid grid-cols-3 gap-2" data-testid="podcast-input-modes">
          {INPUT_MODES.map(m => (
            <button key={m.key} onClick={() => setInputMode(m.key)} data-testid={`podcast-input-${m.key}`}
              className={`flex flex-col items-center gap-1.5 px-3 py-3 rounded-xl border text-center transition-all ${inputMode === m.key
                ? "border-[#FF4400]/50 bg-[#FF4400]/10 text-[#FF4400]"
                : "border-[var(--border)] bg-[var(--bg-card)] text-[var(--text-secondary)] hover:border-[var(--border-strong)]"}`}>
              <m.icon size={20} weight={inputMode === m.key ? "fill" : "regular"} />
              <span className="text-xs font-semibold">{m.label}</span>
              <span className="text-[10px] text-[var(--text-muted)] leading-tight">{m.desc}</span>
            </button>
          ))}
        </div>
      </div>

      {/* ── Input Area (changes based on mode) ── */}
      {inputMode === "topic" && (
        <div>
          <Label>Topic or Prompt</Label>
          <Textarea value={topicText} onChange={e => setTopicText(e.target.value)} rows={4}
            placeholder="e.g., Why market research matters for home service businesses — focus on practical steps small contractors can take without a big budget..."
            className="bg-[var(--bg-card)] border-[var(--border-strong)] text-sm resize-y" data-testid="podcast-topic-input" />
        </div>
      )}

      {inputMode === "upload" && (
        <div>
          <Label>Upload a File</Label>
          <input type="file" ref={fileInputRef} onChange={handleFileSelect} accept=".pdf,.docx,.txt,.md,.json,.csv,.xlsx,.pptx" className="hidden" />
          {uploadedFile ? (
            <div className="flex items-center gap-3 p-3 bg-[var(--bg-card)] border border-emerald-500/30 rounded-xl" data-testid="podcast-uploaded-file">
              <Paperclip size={16} className="text-emerald-400 shrink-0" />
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-[var(--text-primary)] truncate">{uploadedFile.name}</p>
                <p className="text-[10px] text-[var(--text-muted)]">{(uploadedFile.content.length / 1000).toFixed(0)}k characters extracted</p>
              </div>
              <button onClick={() => setUploadedFile(null)} className="p-1 rounded hover:bg-[var(--bg-hover)]"><X size={14} className="text-[var(--text-muted)]" /></button>
            </div>
          ) : (
            <button onClick={() => fileInputRef.current?.click()} disabled={uploading}
              className="w-full flex flex-col items-center gap-2 p-6 bg-[var(--bg-card)] border-2 border-dashed border-[var(--border)] rounded-xl text-[var(--text-muted)] hover:border-[#FF4400]/40 hover:text-[#FF4400] transition-colors cursor-pointer"
              data-testid="podcast-upload-btn">
              {uploading ? <SpinnerGap size={24} className="animate-spin" /> : <CloudArrowUp size={24} />}
              <span className="text-sm">{uploading ? "Processing..." : "Drop or click to upload PDF, Word, TXT, etc."}</span>
            </button>
          )}
        </div>
      )}

      {inputMode === "paste" && (
        <div>
          <Label>Paste Your Content</Label>
          <Textarea value={pasteText} onChange={e => setPasteText(e.target.value)} rows={8}
            placeholder="Paste your article, blog post, research, notes, or any content you want converted into a podcast script..."
            className="bg-[var(--bg-card)] border-[var(--border-strong)] text-sm resize-y font-mono" data-testid="podcast-paste-input" />
          {pasteText.length > 0 && <p className="text-[10px] text-[var(--text-muted)] mt-1 text-right">{(pasteText.length / 1000).toFixed(1)}k chars</p>}
        </div>
      )}

      {/* ── Podcast Mode ── */}
      <div>
        <Label>Podcast Format</Label>
        <div className="grid grid-cols-3 gap-2" data-testid="podcast-format-modes">
          {PODCAST_MODES.map(m => (
            <button key={m.key} onClick={() => handleModeChange(m.key)} data-testid={`podcast-mode-${m.key}`}
              className={`flex flex-col items-center gap-1.5 px-3 py-3 rounded-xl border text-center transition-all ${podcastMode === m.key
                ? "border-violet-500/50 bg-violet-500/10 text-violet-400"
                : "border-[var(--border)] bg-[var(--bg-card)] text-[var(--text-secondary)] hover:border-[var(--border-strong)]"}`}>
              <m.icon size={20} weight={podcastMode === m.key ? "fill" : "regular"} />
              <span className="text-xs font-semibold">{m.label}</span>
              <span className="text-[10px] text-[var(--text-muted)] leading-tight">{m.desc}</span>
            </button>
          ))}
        </div>
      </div>

      {/* ── Speaker Names ── */}
      <div>
        <Label>Speaker{speakerNames.length > 1 ? "s" : ""}</Label>
        <div className="space-y-2">
          {speakerNames.map((name, idx) => (
            <div key={`speaker-${idx}`} className="flex items-center gap-2">
              <Input value={name} onChange={e => updateSpeaker(idx, e.target.value)} placeholder={`Speaker ${idx + 1}`}
                className="bg-[var(--bg-card)] border-[var(--border-strong)] text-sm flex-1" data-testid={`speaker-name-${idx}`} />
              {speakerNames.length > 1 && (
                <Button onClick={() => removeSpeaker(idx)} variant="ghost" size="sm" className="h-9 w-9 p-0 text-red-400 hover:text-red-300">X</Button>
              )}
            </div>
          ))}
          {podcastMode !== "solo" && speakerNames.length < 6 && (
            <Button onClick={addSpeaker} variant="outline" size="sm" className="text-xs" data-testid="add-speaker-btn">+ Add Speaker</Button>
          )}
        </div>
      </div>

      {/* ── Episode Config ── */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <div className="sm:col-span-1">
          <Label>Episode Title</Label>
          <Input value={episodeTitle} onChange={e => setEpisodeTitle(e.target.value)} placeholder="e.g., Why Content Strategy Matters"
            className="bg-[var(--bg-card)] border-[var(--border-strong)] text-sm" data-testid="podcast-title" />
        </div>
        <div>
          <Label>Duration</Label>
          <Select value={String(duration)} onValueChange={v => setDuration(Number(v))}>
            <SelectTrigger className="bg-[var(--bg-card)] border-[var(--border-strong)] text-sm" data-testid="podcast-duration"><SelectValue /></SelectTrigger>
            <SelectContent>{[2, 5, 10, 15, 20, 30, 45, 60].map(d => <SelectItem key={d} value={String(d)}>{d} min</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <div>
          <Label>AI Model</Label>
          <Select value={aiModel} onValueChange={setAiModel}>
            <SelectTrigger className="bg-[var(--bg-card)] border-[var(--border-strong)] text-sm" data-testid="podcast-model"><SelectValue placeholder="Select model" /></SelectTrigger>
            <SelectContent>{availableModels.map(m => <SelectItem key={m} value={m}>{m}</SelectItem>)}</SelectContent>
          </Select>
        </div>
      </div>

      {/* ── Generate Button ── */}
      <Button onClick={handleGenerateScript} disabled={generatingScript || !sourceReady || !aiModel}
        className="w-full h-11 bg-[#FF4400] hover:bg-[#FF6633] text-white font-semibold rounded-xl" data-testid="podcast-gen-script-btn">
        {generatingScript ? <><SpinnerGap size={16} className="animate-spin mr-2" /> Generating Script...</> : <><Microphone size={16} className="mr-2" /> Generate Podcast Script</>}
      </Button>

      {/* ══════════ OUTPUT ══════════ */}
      <AnimatePresence>
        {project && (
          <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="space-y-4">

            {/* Script */}
            <div className="bg-[var(--bg-card)] border border-[var(--border)] rounded-xl p-4">
              <div className="flex items-center justify-between mb-2">
                <p className="text-[10px] font-bold uppercase tracking-wider text-[var(--text-muted)]">Generated Script</p>
                <CopyBtn text={project.script} />
              </div>
              <pre className="text-sm text-[var(--text-secondary)] whitespace-pre-wrap max-h-[300px] overflow-y-auto font-mono leading-relaxed" data-testid="podcast-script-output">
                {project.script}
              </pre>
            </div>

            {/* Voice Assignment */}
            <div className="bg-[var(--bg-card)] border border-[var(--border)] rounded-xl p-4 space-y-3">
              <p className="text-[10px] font-bold uppercase tracking-wider text-[#FF4400]">Assign ElevenLabs Voices</p>
              {loadingVoices ? (
                <p className="text-xs text-[var(--text-muted)]">Loading your voices...</p>
              ) : voices.length === 0 ? (
                <p className="text-xs text-[var(--text-muted)]">No voices found. Add your ElevenLabs API key in Settings, then add voices in your ElevenLabs dashboard.</p>
              ) : (
                Object.keys(voiceAssignments).map(speaker => (
                  <div key={speaker} className="flex items-center gap-3">
                    <span className="text-sm text-[var(--text-primary)] w-28 shrink-0 truncate font-medium">{speaker}</span>
                    <Select value={voiceAssignments[speaker] || "__none__"} onValueChange={v => setVoiceAssignments(prev => ({ ...prev, [speaker]: v === "__none__" ? "" : v }))}>
                      <SelectTrigger className="bg-[var(--bg-base)] border-[var(--border-strong)] text-sm flex-1" data-testid={`voice-select-${speaker}`}>
                        <SelectValue placeholder="Select voice" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="__none__">-- Select a voice --</SelectItem>
                        {voices.map(v => (
                          <SelectItem key={v.voice_id} value={v.voice_id}>
                            {v.name}{v.category ? ` (${v.category})` : ""}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    {voiceAssignments[speaker] && (() => {
                      const voice = voices.find(v => v.voice_id === voiceAssignments[speaker]);
                      return voice?.preview_url ? (
                        <Button onClick={() => playPreview(voice.preview_url)} variant="ghost" size="sm" className="h-8 w-8 p-0">
                          <SpeakerHigh size={14} className={playingPreview === voice.preview_url ? "text-[#FF4400]" : ""} />
                        </Button>
                      ) : null;
                    })()}
                  </div>
                ))
              )}
              <Button onClick={handleGenerateAudio} disabled={generatingAudio || voices.length === 0}
                className="w-full h-11 bg-emerald-600 hover:bg-emerald-500 text-white font-semibold rounded-xl mt-2" data-testid="podcast-gen-audio-btn">
                {generatingAudio ? <><SpinnerGap size={16} className="animate-spin mr-2" /> Generating Audio...</> : <><SpeakerHigh size={16} className="mr-2" /> Generate Audio</>}
              </Button>
            </div>

            {/* Audio Segments + Merge */}
            {audioSegments.length > 0 && (
              <div className="bg-[var(--bg-card)] border border-emerald-500/20 rounded-xl p-4 space-y-3">
                <p className="text-[10px] font-bold uppercase tracking-wider text-emerald-400">Audio Segments ({successfulSegments.length}/{audioSegments.length})</p>
                <div className="space-y-1.5 max-h-[250px] overflow-y-auto">
                  {audioSegments.map((seg, i) => (
                    <div key={`seg-${i}`} className="flex items-center gap-3 p-2 rounded-lg bg-[var(--bg-base)] border border-[var(--border)]" data-testid={`audio-segment-${i}`}>
                      <span className="text-xs font-medium text-[var(--text-primary)] w-20 shrink-0">{seg.speaker}</span>
                      {seg.error ? (
                        <span className="text-xs text-red-400 flex-1">{seg.error}</span>
                      ) : (
                        <>
                          <span className="text-[10px] text-[var(--text-muted)] flex-1 truncate">{seg.text_preview}</span>
                          <Button onClick={() => playSegment(seg.audio_base64)} variant="ghost" size="sm" className="h-7 w-7 p-0"><Play size={12} /></Button>
                          <Button onClick={() => downloadBlob(b64ToBlob(seg.audio_base64), `segment-${seg.index}-${seg.speaker}.mp3`)} variant="ghost" size="sm" className="h-7 w-7 p-0"><Download size={12} /></Button>
                        </>
                      )}
                    </div>
                  ))}
                </div>

                {/* Format selector + Merge */}
                <div className="border-t border-[var(--border)] pt-3 space-y-3">
                  <div>
                    <Label>Export Format</Label>
                    <div className="grid grid-cols-3 gap-2" data-testid="podcast-audio-formats">
                      {AUDIO_FORMATS.map(f => (
                        <button key={f.key} onClick={() => { setAudioFormat(f.key); setMergedAudio(null); }}
                          data-testid={`audio-fmt-${f.key}`}
                          className={`px-3 py-2 rounded-lg border text-center transition-all ${audioFormat === f.key
                            ? "border-violet-500/50 bg-violet-500/10 text-violet-400"
                            : "border-[var(--border)] bg-[var(--bg-base)] text-[var(--text-secondary)]"}`}>
                          <span className="text-xs font-semibold">{f.label}</span>
                          <span className="block text-[10px] text-[var(--text-muted)]">{f.desc}</span>
                        </button>
                      ))}
                    </div>
                  </div>

                  {!mergedAudio ? (
                    <Button onClick={handleMergeAudio} disabled={merging || successfulSegments.length === 0}
                      className="w-full h-10 bg-violet-600 hover:bg-violet-500 text-white font-semibold rounded-xl" data-testid="podcast-merge-btn">
                      {merging ? <><SpinnerGap size={16} className="animate-spin mr-2" /> Merging as {audioFormat.toUpperCase()}...</> : <><MusicNote size={16} className="mr-2" /> Merge into Full Episode ({audioFormat.toUpperCase()})</>}
                    </Button>
                  ) : (
                    <div className="flex items-center gap-2 p-3 bg-violet-500/10 border border-violet-500/30 rounded-xl">
                      <Button onClick={togglePlayEpisode} variant="ghost" size="sm" className="h-9 w-9 p-0 text-violet-400" data-testid="podcast-play-episode">
                        {playingEpisode ? <Pause size={16} weight="fill" /> : <Play size={16} weight="fill" />}
                      </Button>
                      <div className="flex-1">
                        <p className="text-sm font-medium text-[var(--text-primary)]">{episodeTitle || "Full Episode"}</p>
                        <p className="text-[10px] text-[var(--text-muted)]">
                          {Math.floor(mergedAudio.duration_seconds / 60)}:{String(Math.round(mergedAudio.duration_seconds % 60)).padStart(2, "0")} / {mergedAudio.file_size_kb} KB / {mergedAudio.file_extension?.toUpperCase()}
                        </p>
                      </div>
                      <Button onClick={() => {
                        const mime = mergedAudio.mime_type || "audio/mpeg";
                        const ext = mergedAudio.file_extension || "mp3";
                        downloadBlob(b64ToBlob(mergedAudio.merged_audio_base64, mime), `${(episodeTitle || "episode").replace(/\s+/g, "-").toLowerCase()}.${ext}`);
                      }}
                        className="h-9 bg-violet-600 hover:bg-violet-500 text-white text-xs px-3" data-testid="podcast-download-episode">
                        <Download size={14} className="mr-1.5" /> Download {mergedAudio.file_extension?.toUpperCase()}
                      </Button>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Show Notes + Export */}
            <div className="bg-[var(--bg-card)] border border-[var(--border)] rounded-xl p-4 space-y-3">
              <div className="flex items-center justify-between">
                <p className="text-[10px] font-bold uppercase tracking-wider text-[var(--text-muted)]">Show Notes & SEO</p>
                {showNotes && (
                  <div className="flex items-center gap-1">
                    <CopyBtn text={showNotes} />
                    <button onClick={() => downloadShowNotes("md")} className="px-2 py-1 text-[10px] rounded bg-[var(--bg-base)] text-[var(--text-muted)] hover:text-[var(--text-primary)] border border-[var(--border)]" data-testid="notes-export-md">.md</button>
                    <button onClick={() => downloadShowNotes("txt")} className="px-2 py-1 text-[10px] rounded bg-[var(--bg-base)] text-[var(--text-muted)] hover:text-[var(--text-primary)] border border-[var(--border)]" data-testid="notes-export-txt">.txt</button>
                    <button onClick={() => downloadShowNotes("json")} className="px-2 py-1 text-[10px] rounded bg-[var(--bg-base)] text-[var(--text-muted)] hover:text-[var(--text-primary)] border border-[var(--border)]" data-testid="notes-export-json">.json</button>
                  </div>
                )}
              </div>
              {!showNotes ? (
                <Button onClick={handleGenerateShowNotes} disabled={generatingNotes || !aiModel}
                  className="w-full h-10 bg-[var(--bg-base)] hover:bg-[var(--bg-hover)] border border-[var(--border)] text-[var(--text-primary)] font-medium rounded-xl" data-testid="podcast-gen-notes-btn">
                  {generatingNotes ? <><SpinnerGap size={16} className="animate-spin mr-2" /> Generating...</> : <><FileText size={16} className="mr-2" /> Generate Show Notes & SEO Metadata</>}
                </Button>
              ) : (
                <div className="prose prose-sm prose-invert max-w-none text-[var(--text-secondary)]
                  prose-headings:text-[var(--text-primary)] prose-headings:font-semibold prose-headings:mt-4 prose-headings:mb-1
                  prose-strong:text-[var(--text-primary)] prose-p:my-1.5 prose-ul:my-1.5 prose-li:my-0.5
                  prose-blockquote:border-l-[#FF4400]" data-testid="podcast-show-notes">
                  <ReactMarkdown>{showNotes}</ReactMarkdown>
                </div>
              )}
            </div>

          </motion.div>
        )}
      </AnimatePresence>

      </>
      )}
    </div>
  );
}
